import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAppDispatch } from "../../store/hooks";
import {
  fetchLiveRedesignReport,
  type RdPercentageSnapshot,
} from "../../store/superAdmin/dataExtractSlice";
import { useToast } from "../../components/utilities/Toast";
import { downloadLiveRedesignExcel } from "../../utils/liveRedesignExcel";
import { CustomDatePicker } from "../../components/common/DatePickers";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEARS = [2024, 2025, 2026, 2027];
const fieldClass =
  "rounded-lg border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2 text-sm text-[#4A4A48] focus:border-[#2fa9bf] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2fa9bf]/20";
const labelClass = "mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-[#8A8A85]";

type PeriodKey = "day" | "week" | "month" | "year" | "custom";

const PERIODS: { id: PeriodKey; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
  { id: "custom", label: "Custom" },
];

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (day: number, month: number, year: number) =>
  `${year}-${pad(month)}-${pad(day)}`;
const addCalendarDays = (date: string, days: number) => {
  const [year, month, day] = date.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
};
const mondayOf = (date: string) => {
  const [year, month, day] = date.split("-").map(Number);
  const jsDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return addCalendarDays(date, -(jsDay === 0 ? 6 : jsDay - 1));
};
const monthEndOf = (year: number, month: number) => {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${pad(month)}-${pad(last)}`;
};

type WeekOption = { id: number; label: string; from: string; to: string };

const weeksInMonth = (year: number, month: number): WeekOption[] => {
  const monthStart = `${year}-${pad(month)}-01`;
  const monthEnd = monthEndOf(year, month);
  let cursor = mondayOf(monthStart);
  const weeks: WeekOption[] = [];
  let n = 1;
  while (cursor <= monthEnd) {
    const weekEnd = addCalendarDays(cursor, 6);
    const from = cursor < monthStart ? monthStart : cursor;
    const to = weekEnd > monthEnd ? monthEnd : weekEnd;
    weeks.push({
      id: n,
      label: `Week ${n} (${from.slice(8)}–${to.slice(8)})`,
      from,
      to,
    });
    n += 1;
    cursor = addCalendarDays(cursor, 7);
  }
  return weeks;
};

const formatPct = (value: number | null | undefined) =>
  value == null ? "—" : `${value}%`;

const DownloadIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
  </svg>
);

export const LiveRedesignReportCard = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const today = new Date();
  const todayStr = ymd(today.getDate(), today.getMonth() + 1, today.getFullYear());
  const initialWeeks = weeksInMonth(today.getFullYear(), today.getMonth() + 1);
  const initialWeek =
    initialWeeks.find((week) => week.from <= todayStr && todayStr <= week.to) || initialWeeks[0];

  const [viewPeriod, setViewPeriod] = useState<PeriodKey>("day");
  const [day, setDay] = useState(today.getDate());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [weekId, setWeekId] = useState(initialWeek?.id || 1);
  const [customFrom, setCustomFrom] = useState(
    `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
  );
  const [customTo, setCustomTo] = useState(
    `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
  );
  const [exportKeys, setExportKeys] = useState<PeriodKey[]>(["day"]);
  const [snapshot, setSnapshot] = useState<RdPercentageSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const weeks = useMemo(() => weeksInMonth(year, month), [year, month]);
  const selectedWeek = weeks.find((week) => week.id === weekId) || weeks[0];

  useEffect(() => {
    if (!weeks.some((week) => week.id === weekId) && weeks[0]) {
      setWeekId(weeks[0].id);
    }
  }, [weeks, weekId]);

  const rangeFor = (key: PeriodKey) => {
    const date = ymd(day, month, year);
    switch (key) {
      case "day":
        return { key, label: `Day ${date}`, fromDate: date, toDate: date };
      case "week":
        return {
          key,
          label: `${MONTHS[month - 1]} ${year} ${selectedWeek?.label || "Week"}`,
          fromDate: selectedWeek?.from || date,
          toDate: selectedWeek?.to || date,
        };
      case "month":
        return {
          key,
          label: `${MONTHS[month - 1]} ${year}`,
          fromDate: `${year}-${pad(month)}-01`,
          toDate: monthEndOf(year, month),
        };
      case "year":
        return {
          key,
          label: `Year ${year}`,
          fromDate: `${year}-01-01`,
          toDate: `${year}-12-31`,
        };
      default:
        return {
          key,
          label: customFrom === customTo ? `Custom ${customFrom}` : `Custom ${customFrom} to ${customTo}`,
          fromDate: customFrom,
          toDate: customTo,
        };
    }
  };

  const viewRange = rangeFor(viewPeriod);

  useEffect(() => {
    if (viewPeriod === "custom" && (!customFrom || !customTo || customFrom > customTo)) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    dispatch(fetchLiveRedesignReport({ ranges: [viewRange] }))
      .unwrap()
      .then((data) => {
        if (!cancelled) setSnapshot(data.snapshots?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) setSnapshot(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // viewRange is rebuilt each render; depend on the primitive inputs instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    viewPeriod,
    day,
    month,
    year,
    selectedWeek?.from,
    selectedWeek?.to,
    customFrom,
    customTo,
  ]);

  const toggleExportKey = (key: PeriodKey) => {
    setExportKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  const downloadSelected = async () => {
    if (!exportKeys.length) {
      showToast("Select at least one period to export", "error");
      return;
    }
    if (exportKeys.includes("custom") && (!customFrom || !customTo || customFrom > customTo)) {
      showToast("Custom from date cannot be after to date", "error");
      return;
    }
    setExporting(true);
    try {
      const ranges = exportKeys.map((key) => rangeFor(key));
      const report = await dispatch(fetchLiveRedesignReport({ ranges })).unwrap();
      const snapshots = report.snapshots || [];
      if (!snapshots.length) {
        showToast("No redesign report data for the selected periods", "error");
        return;
      }
      await downloadLiveRedesignExcel(snapshots, `Live_Redesign_Report_${viewRange.fromDate}.xlsx`);
      showToast("Export downloaded successfully", "success");
    } catch (error) {
      console.error(error);
      showToast("Something went wrong", "error");
    } finally {
      setExporting(false);
    }
  };

  const counts = snapshot?.counts;
  const pct = snapshot?.percentages;
  const labs = snapshot?.labs || [];
  const chartPct = [
    { name: "Total", value: Number(pct?.total_rd_of_live || 0), fill: "#c2410c" },
    { name: "Internal", value: Number(pct?.internal_of_live || 0), fill: "#8b5cf6" },
    { name: "Client", value: Number(pct?.client_of_live || 0), fill: "#0ea5e9" },
  ];
  const chartCounts = [
    { name: "Live", value: Number(counts?.live || 0), fill: "#0f766e" },
    { name: "Total RD", value: Number(counts?.total_redesigns || 0), fill: "#c2410c" },
    { name: "Internal", value: Number(counts?.internal || 0), fill: "#8b5cf6" },
    { name: "Client", value: Number(counts?.client || 0), fill: "#0ea5e9" },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-[#E8E8E4] bg-white shadow-sm">
      <div className="border-b border-rose-100 bg-rose-50 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-[#4A4A48]">Live Redesign Report</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#8A8A85]">
              Live redesign percentage for a day, a week of any month, a month, a year, or a custom
              range. Client RD still counts only Note Missed, Design Mistake, Files Mistake, and
              Client Preference Missed.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        <div className="flex flex-wrap gap-1 rounded-xl bg-[#F8F8F6] p-1">
          {PERIODS.map((period) => (
            <button
              key={period.id}
              type="button"
              onClick={() => setViewPeriod(period.id)}
              className={`rounded-lg px-3 py-1.5 text-[11px] font-medium ${
                viewPeriod === period.id
                  ? "bg-white text-[#2fa9bf] shadow-sm"
                  : "text-[#6B6B66] hover:text-[#4A4A48]"
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {viewPeriod === "day" ? (
            <>
              <div>
                <p className={labelClass}>Day</p>
                <select value={day} onChange={(e) => setDay(Number(e.target.value))} className={fieldClass}>
                  {[...Array(31)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={labelClass}>Month</p>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={fieldClass}>
                  {MONTHS.map((name, i) => (
                    <option key={name} value={i + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={labelClass}>Year</p>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={fieldClass}>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          {viewPeriod === "week" ? (
            <>
              <div>
                <p className={labelClass}>Month</p>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={fieldClass}>
                  {MONTHS.map((name, i) => (
                    <option key={name} value={i + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={labelClass}>Year</p>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={fieldClass}>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={labelClass}>Week</p>
                <select value={selectedWeek?.id || 1} onChange={(e) => setWeekId(Number(e.target.value))} className={fieldClass}>
                  {weeks.map((week) => (
                    <option key={week.id} value={week.id}>{week.label}</option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          {viewPeriod === "month" ? (
            <>
              <div>
                <p className={labelClass}>Month</p>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={fieldClass}>
                  {MONTHS.map((name, i) => (
                    <option key={name} value={i + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={labelClass}>Year</p>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={fieldClass}>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          {viewPeriod === "year" ? (
            <div>
              <p className={labelClass}>Year</p>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={fieldClass}>
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          ) : null}

          {viewPeriod === "custom" ? (
            <>
              <CustomDatePicker
                label="From"
                value={customFrom}
                max={customTo}
                onChange={setCustomFrom}
              />
              <CustomDatePicker
                label="To"
                value={customTo}
                min={customFrom}
                onChange={setCustomTo}
              />
            </>
          ) : null}

          <p className="pb-2 text-[11px] text-[#8A8A85]">
            {viewRange.fromDate === viewRange.toDate
              ? viewRange.fromDate
              : `${viewRange.fromDate} → ${viewRange.toDate}`}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: "Live cases", value: loading ? "—" : counts?.live ?? 0 },
            { label: "Total redesigns", value: loading ? "—" : counts?.total_redesigns ?? 0 },
            { label: "Total RD %", value: loading ? "—" : formatPct(pct?.total_rd_of_live) },
            {
              label: "Internal / Client RD %",
              value: loading
                ? "—"
                : `${formatPct(pct?.internal_of_live)} / ${formatPct(pct?.client_of_live)}`,
            },
          ].map((item) => (
            <div key={item.label} className="rounded-2xl border border-[#E8E8E4] bg-[#F8F8F6] px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">{item.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-[#4A4A48]">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-[#E8E8E4] p-4">
            <p className={labelClass}>Redesign %</p>
            <div className="h-56">
              {loading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8A8A85]">Loading…</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartPct}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8A8A85" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#8A8A85" }} />
                    <Tooltip formatter={(value) => `${Number(value || 0)}%`} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {chartPct.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-[#E8E8E4] p-4">
            <p className={labelClass}>Cases</p>
            <div className="h-56">
              {loading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8A8A85]">Loading…</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartCounts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E4" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8A8A85" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#8A8A85" }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {chartCounts.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-[#E8E8E4]">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#F8F8F6] text-left">
                <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">Lab</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">Cases</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">Redesigns</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">RD %</th>
              </tr>
            </thead>
            <tbody>
              {loading && !labs.length ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-[#8A8A85]">Loading lab-wise RD %…</td>
                </tr>
              ) : labs.length ? (
                labs.map((row) => (
                  <tr key={row.lab} className="border-b border-[#F0F0EC] last:border-0">
                    <td className="px-4 py-2 font-medium text-[#4A4A48]">{row.lab}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#4A4A48]">{Number(row.total_cases || 0)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#4A4A48]">{Number(row.total_redesigns || 0)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#4A4A48]">{formatPct(row.rd_pct)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-[#8A8A85]">No lab-wise RD data for this window</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl border border-[#E8E8E4] bg-[#F8F8F6] px-4 py-4">
          <p className={labelClass}>Excel export</p>
          <p className="mb-3 text-xs text-[#8A8A85]">
            Select one or more periods. The workbook includes a summary with charts plus a sheet for each selected period.
          </p>
          <div className="mb-3 flex flex-wrap gap-2">
            {PERIODS.map((period) => {
              const selected = exportKeys.includes(period.id);
              return (
                <button
                  key={period.id}
                  type="button"
                  onClick={() => toggleExportKey(period.id)}
                  className={`rounded-lg px-3 py-1.5 text-[11px] font-medium ${
                    selected
                      ? "bg-white text-[#2fa9bf] shadow-sm"
                      : "text-[#6B6B66] hover:text-[#4A4A48]"
                  }`}
                >
                  {selected ? "✓ " : ""}
                  {period.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={downloadSelected}
            disabled={exporting || !exportKeys.length}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2fa9bf] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {exporting ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <DownloadIcon />
            )}
            {exporting ? "Exporting…" : "Download selected reports"}
          </button>
        </div>
      </div>
    </section>
  );
};
